import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronRight } from "lucide-react";
import { handleUnauthorizedAccess } from "@/lib/access-control";
import { getUploadUrl, getCloudinaryThumbnail } from "@/lib/utils";
export function ParticipantLink({ participant, isAuthenticated }) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [canAccess, setCanAccess] = useState(false);
  const [checkDone, setCheckDone] = useState(false);
  const [location, setLocation] = useState(null);

  const clubRole = participant.club_role;
  const isClubTrip = clubRole !== undefined;
  const isClubMember = isClubTrip && !!clubRole;

  useEffect(() => {
    if (participant.user_id) {
      (async () => {
        try {
          const res = await api.get(`/api/motard/${participant.user_id}/access-check`);
          setCanAccess(res.data.can_access === true);
          const city = res.data.city;
          const region = res.data.region;
          if (city || region) setLocation([city, region].filter(Boolean).join(", "));
        } catch { setCanAccess(false); }
        finally { setCheckDone(true); }
      })();
    } else {
      setCheckDone(true);
    }
  }, [participant.user_id]);

  const handleClick = () => {
    if (!isAuthenticated) {
      handleUnauthorizedAccess(navigate, { message: t("trip_detail.auth_required.profile") });
      window.scrollTo(0, 0);
      return;
    }
    if (participant.user_id && canAccess) navigate(`/meu-canto?motard=${participant.user_id}`);
  };

  const isClickable = checkDone && participant.user_id && canAccess;
  const moto = participant.motorcycle || participant.motorcycle_snapshot;
  const displayName = participant.nickname || participant.name;

  return (
    <div
      className={`flex items-center p-2.5 bg-muted/30 rounded-xl transition-colors gap-3 ${isClickable ? "cursor-pointer hover:bg-muted/50" : ""}`}
      onClick={isClickable ? handleClick : undefined}
      data-testid={`participant-${participant.participant_id}`}
    >
      <Avatar className="h-10 w-10 border-2 border-border flex-shrink-0">
        <AvatarImage
          src={getCloudinaryThumbnail(participant.user_picture || (participant.photo ? getUploadUrl(participant.photo) : undefined), 80, 80)}
          alt={displayName}
        />
        <AvatarFallback className="bg-primary/10 text-primary font-bold">
          {displayName.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm truncate">{displayName}</p>
        {moto && moto.brand && (
          <p className="text-xs text-primary/80 truncate mt-0.5">{[moto.brand, moto.model].filter(Boolean).join(" ")}</p>
        )}
        {location && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">{location}</p>
        )}
        {isClubTrip && (
          <span className={`inline-flex items-center text-[10px] font-medium mt-1 px-1.5 py-0.5 rounded-md ${
            isClubMember 
              ? "bg-primary/15 text-primary border border-primary/20" 
              : "bg-zinc-500/15 text-zinc-400 border border-zinc-500/20"
          }`}>
            {isClubMember ? t("trip_detail.participant_club_member", "Membro do Clube") : t("trip_detail.participant_guest", "Convidado")}
          </span>
        )}
      </div>
      {moto?.photo && (
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <div className="h-16 w-24 rounded-lg overflow-hidden border border-border flex-shrink-0">
              <img
                loading="lazy"
                src={getCloudinaryThumbnail(moto.photo.startsWith("http") ? moto.photo : getUploadUrl(moto.photo), 192, 128)}
                alt={[moto.brand, moto.model].filter(Boolean).join(" ")}
                className="h-full w-full object-cover"
                onError={(e) => { e.target.parentElement.style.display = "none"; }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="font-medium">{[moto.brand, moto.model].filter(Boolean).join(" ")}</p>
            {moto.year && <p className="text-xs opacity-80">{moto.year}</p>}
          </TooltipContent>
        </Tooltip>
      )}
      {isClickable && <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
    </div>
  );
}
